import { useEffect } from "react";
import { Form, Input, Modal, Select } from "antd";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { NC_SEVERITIES, SECS, type NcDto } from "@audit-plan/shared";
import { api } from "../../api/client";
import { usePeople } from "../../api/people";
import { invalidateAudit, useAudit, useQuestions } from "../../api/audits";
import { useFullModal } from "../audit/mobileModal";
import { useIsMobile } from "../useIsMobile";
import { useNotify } from "../audit/useNotify";

interface V {
  title: string;
  severity: NcDto["severity"];
  ownerId: string;
  questionId?: string | null;
  description: string;
}

/** NC manuale (auditId) o modifica (nc). */
export function NcFormModal(props: { open: boolean; onClose: () => void; auditId?: string; nc?: NcDto; onSaved?: (n: NcDto) => void }) {
  const { open, onClose, nc, onSaved } = props;
  const auditId = props.auditId ?? nc?.auditId;
  const { t, i18n } = useTranslation();
  const fm = useFullModal();
  const mob = useIsMobile();
  const en = i18n.language === "en";
  const [form] = Form.useForm<V>();
  const qc = useQueryClient();
  const n = useNotify();
  const people = usePeople();
  const audit = useAudit(auditId).data;
  const qs = (useQuestions(audit?.checklistVersionId).data ?? []).filter((q) => (SECS as readonly string[]).includes(q.section));

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({
      title: nc?.title ?? "",
      severity: nc?.severity ?? "minor",
      ownerId: nc?.ownerId ?? audit?.auditorId,
      questionId: nc?.questionId ?? null,
      description: nc?.description ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, nc?.id, audit?.id]);

  const save = useMutation({
    mutationFn: (v: V) => {
      const body = { ...v, questionId: v.questionId || null };
      return nc ? api.patch<NcDto>(`/ncs/${nc.id}`, body) : api.post<NcDto>(`/audits/${auditId}/ncs`, body);
    },
    onSuccess: (r) => {
      n.ok();
      invalidateAudit(qc, auditId);
      onSaved?.(r);
      onClose();
    },
    onError: n.fail,
  });
  const req = { required: true, message: t("required") };
  return (
    <Modal {...fm} open={open} title={nc ? `${t("edit")} ${nc.code}` : t("newNc")} onCancel={onClose} okText={t("save")} cancelText={t("cancel")} confirmLoading={save.isPending} onOk={() => form.validateFields().then((v) => save.mutate(v))} {...(mob ? {} : { width: 640 })} destroyOnHidden>
      <Form form={form} layout="vertical">
        <Form.Item name="title" label={t("title")} rules={[req]}>
          <Input />
        </Form.Item>
        <div style={{ display: "grid", gridTemplateColumns: mob ? "1fr" : "1fr 1fr", gap: "0 16px" }}>
          <Form.Item name="severity" label={t("severity")}>
            <Select options={NC_SEVERITIES.map((s) => ({ value: s, label: t(`sev_${s}`) }))} />
          </Form.Item>
          <Form.Item name="ownerId" label={t("owner")} rules={[req]}>
            <Select options={people.options(["auditor", "manager", "admin"])} showSearch optionFilterProp="label" />
          </Form.Item>
        </div>
        <Form.Item name="questionId" label={t("question")}>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="—"
            options={qs.map((q) => ({ value: q.id, label: `${q.code} · ${(en ? q.en || q.it : q.it).slice(0, 70)}` }))}
          />
        </Form.Item>
        <Form.Item name="description" label={t("description")} rules={[req]}>
          <Input.TextArea rows={4} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
